import { Filter, getLoanById, LoanStatus } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { SWRConfig } from 'swr';

import { useFetchLoanList } from '@/components/modules/LoanListByProduct/hooks/useFetchLoanList';
import { LoanListSupportedApplicationType } from '@/components/modules/LoanListByProduct/types/LoanListSupportedApplicationType';
import { LoanDataGridFields } from '@/components/modules/LoanListByProduct/utils/LoanDataGridFields/LoanDataGridFields';
import { DetailedLoanStatusTag } from '@/components/shared/components/DetailedLoanStatusTag';
import { MultiFilteredList } from '@/components/shared/components/MultiFilteredList';
import { Text } from '@/components/shared/components/Text';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { ALLOWED_LOAN_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/config/loanConfig';
import { ApplicationTypeMap } from '@/config/maps/ApplicationType.map';
import { ApplicationType } from '@/config/types';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useCache } from '@/hooks/requests/useCache';
import { useCollectionScope } from '@/hooks/useCollectionScope';
import { forceRefreshDataGridTable } from '@/utils/forceRefreshDataGridTable';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

interface LoanListByApplicationTypeProps {
  applicationType: LoanListSupportedApplicationType;
  listProps?: {
    additionalFilters?: Filter[];
    reducedWidth?: boolean;
  };
}

export const LoanListByApplicationType = ({ applicationType, listProps }: LoanListByApplicationTypeProps) => {
  const {
    state: { lastViewedLoanId },
    actions: { updateOperationsContext },
  } = useOperationsContext();

  const scope = useCollectionScope();
  const loan = lastViewedLoanId ? useCache(getLoanById, { loanId: lastViewedLoanId })?.data : undefined;

  const navigate = useNavigate();
  const { fetchLoanList, fetchXLSXExport } = useFetchLoanList({
    applicationType,
    additionalFilters: listProps?.additionalFilters,
  });

  useEffect(() => {
    forceRefreshDataGridTable();
  }, [applicationType]);

  const itemsValues = ALLOWED_LOAN_STATUSES_FOR_MULTI_SELECT_FILTER;

  const getDetailPath = () => {
    switch (applicationType) {
      case ApplicationType.CONSUMER_LOAN:
        return DYNAMIC_PAGE_PATHS.CONSUMER_LOAN;
      case ApplicationType.CREDIT_LINE:
        return DYNAMIC_PAGE_PATHS.CREDIT_LINE;
      case ApplicationType.CARD:
        return DYNAMIC_PAGE_PATHS.CARD;
    }
  };

  const detailPath = getDetailPath();
  const liveResource = ApplicationTypeMap.toListResource(applicationType);

  return (
    <SWRConfig value={{ revalidateIfStale: false }}>
      <MultiFilteredList
        fetchList={fetchLoanList}
        getXLSXExport={fetchXLSXExport}
        dataGridFields={LoanDataGridFields({ applicationType })}
        liveUpdates={liveResource ? { resource: liveResource, scope } : undefined}
        onRowClick={(selectedRow) => {
          updateOperationsContext({ lastViewedLoanId: selectedRow.id });
          detailPath && navigate(generatePath(detailPath, { loanId: selectedRow.id }));
        }}
        getRowClassName={(params) => {
          if (params.row.id === loan?.id) return 'last-viewed-application';
          else return '';
        }}
        multiselectProps={{
          field: 'status',
          label: <Text tId={'STATUS'} />,
          defaultLabel: <Text tId={'DEFAULT_STATUSES'} />,
          items: itemsValues,
          checkBoxRender: (value) => {
            return <DetailedLoanStatusTag status={value} applicationType={applicationType} />;
          },
          filtersValueHandler: (valueArray: string[]) => {
            if (valueArray?.includes(LoanStatus.STATUS_ACTIVE)) {
              valueArray.push(LoanStatus.STATUS_ACTIVE_TRIAL);
            }
            if (valueArray?.includes(LoanStatus.STATUS_CANCELLED)) {
              valueArray.push(LoanStatus.STATUS_CANCELLED_TEST);
            }
            return valueArray;
          },
          defaultItems: [
            LoanStatus.STATUS_INITIAL,
            LoanStatus.STATUS_ANALYSING,
            LoanStatus.STATUS_LENDER_HAS_BEEN_SELECTED,
            LoanStatus.STATUS_CONTRACT_SIGNED,
            LoanStatus.STATUS_DOCS_APPROVED,
            LoanStatus.STATUS_ACTIVE,
            LoanStatus.STATUS_DENIED,
            LoanStatus.STATUS_DESISTED,
            LoanStatus.STATUS_CANCELLED,
          ],
        }}
        dateFilterProps={{
          dateField: 'updated_at',
        }}
        defaultOrderBy={'updated_at'}
        showExport
        disableSelectionOnClick
      />
    </SWRConfig>
  );
};

export const LoanListByApplicationTypeModule = withPermissionsModule(LoanListByApplicationType);
